import dropdownEnums, {
  Enums,
  enum_salutations,
} from "@/services/dropdown-enums";
import {
  camelCaseStringToNormalText,
  camelize,
  generateOptionsDefault,
  getFormattedDate,
  generateOptions,
} from "@/services/generic.methods";
import SelectFilter from "@inovua/reactdatagrid-community/SelectFilter";
import DateFilter from "@inovua/reactdatagrid-community/DateFilter";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import UIButton from "../ui-controls/button";
import store from "@/store";

import { faGripVertical } from "@fortawesome/free-solid-svg-icons/faGripVertical";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TagsChips from "../ui-controls/tags-input/tags-chips";
import { time } from "@/modules/crm/global/constants";
import TimeFilter from "./TimeFilter";
import GridDateFilter from "./GridDateFilter";
import GridIdField from "../grid-id-field";
import { GraphColors } from "../charts/helper";
import TruncateTooltip from "../truncate-with-tooltip";
import { resolveFieldByColumnName } from "@/services/custom-fields/fieldKeys";

const DATE_COLUMN_TYPES = ["date", "datetime", "datetime?", "datetimeoffset"];

const isDateColumnType = (columnType = "") =>
  DATE_COLUMN_TYPES.includes(String(columnType).toLowerCase());

/** Display format for a Date / Time / Date & Time custom field. */
const getCustomFieldDateDisplayFormat = (field) => {
  if (!field) {
    return null;
  }

  const subType = String(field.subType || "").toLowerCase();
  const fieldSubType = Number(field.fieldSubType);
  const isDateTimeField =
    field.fieldType === Enums.CustomFieldType.Datetime ||
    String(field.parentType || "").toLowerCase() === "datetime" ||
    fieldSubType === Enums.CustomFieldSubType.Date ||
    fieldSubType === Enums.CustomFieldSubType.Time ||
    fieldSubType === Enums.CustomFieldSubType.DateAndTime ||
    subType === "date" ||
    subType === "time" ||
    subType === "dateandtime";

  if (!isDateTimeField) {
    return null;
  }

  if (
    subType === "dateandtime" ||
    fieldSubType === Enums.CustomFieldSubType.DateAndTime
  ) {
    return {
      dateFormat: time.format.default,
      fetchDateFormat: time.format.fetchDate,
      isUTC: false,
    };
  }

  if (subType === "time" || fieldSubType === Enums.CustomFieldSubType.Time) {
    return { dateFormat: time.format.time };
  }

  return { dateFormat: time.format.date };
};

const renderFormattedDate = (col, value) =>
  value
    ? getFormattedDate(value, col?.dateFormat || "DD/MM/YYYY", col?.isUTC)
    : "N/A";

export const getHeader = (title, draggable = true) => {
  return (
    <span
      className="header-drag-container align-items-center"
      style={{ display: "flex" }}
    >
      {draggable ? (
        <FontAwesomeIcon
          className="header-drag-icon"
          icon={faGripVertical}
          style={{ marginRight: "5px" }}
        />
      ) : null}
      <span
        className={draggable ? "header-drag-text" : ""}
        style={{ fontWeight: "500" }}
      >
        {title}{" "}
      </span>
    </span>
  );
};
export const DynamicColGenerator = (
  columnsMeta = [],
  defaultVisibleCols = [],
  garbageColumnHeaderText = [],
  rowClick,
  customFields
) => {
  let columns = [];
  columnsMeta.forEach((column, index) => {
    let columnKey = camelize(column.columnName);
    if (columnKey === "leadTypeId") {
      column.columnType = "select";
    }
    let headerText = column?.columnDisplayName
      ? column?.columnDisplayName
      : camelCaseStringToNormalText(column?.columnName, "heading");

    const type = resolveFieldByColumnName(customFields, column?.columnName);
    const dateDisplay = getCustomFieldDateDisplayFormat(type);
    // columnType "datetime" (Date and Date & Time fields) never matched the
    // old "date"-only check, so cells rendered the raw ISO value.
    if (dateDisplay) {
      column.dateFormat = dateDisplay.dateFormat;
      if (dateDisplay.fetchDateFormat) {
        column.fetchDateFormat = dateDisplay.fetchDateFormat;
      }
      if (dateDisplay.isUTC !== undefined) {
        column.isUTC = dateDisplay.isUTC;
      }
      if (!isDateColumnType(column?.columnType)) {
        column.formatAsDate = true;
      }
    }
    if (customFields) {
      column.customFields = customFields;
    }
    let col = {
      ...column,
      visible: defaultVisibleCols.length
        ? defaultVisibleCols.some(
            (dc) => dc.toLowerCase() == columnKey.toLowerCase()
          )
        : true,
      name: columnKey, //initiatedby
      header: (
        <span style={{ fontWeight: "500" }}>
          {column.header || getHeader(headerText, column.draggable)}
        </span>
      ),
      headerProps: { className: "hide-filter-button", headerText },
      ...(column?.flex === false ? {} : { defaultFlex: 1 }),
      ...(column?.width ? { width: column?.width } : {}),
      minWidth: column.minWidth ?? (headerText || "").length * 10 + 50,
      filterDelay: 800,
      group: column?.group || "",
      draggable: column.draggable ?? true,
      enableFilter: column?.enableFilter ?? true,
      filter: true,
      sortable: column?.sortable ?? true,
      ...getFiltersByType({
        ...column,
        header: column.header || headerText,
        customFields,
      }),
      render: ({ value, data }) => {
        const isRowClickable = typeof rowClick === "function";
        return (
          <Box
            className={`d-flex justify-content-space-between align-items-center preview-container ${column.className}`}
            sx={{
              p: "8px",
              gap: "5px",
              ...(isRowClickable ? { cursor: "pointer" } : {}),
            }}
            onClick={
              isRowClickable
                ? (e) => {
                    if (
                      e?.target?.closest?.(
                        ".InovuaReactDataGrid__column-header"
                      )
                    ) {
                      return;
                    }
                    rowClick(data, e);
                  }
                : undefined
            }
          >
            {column.render ? (
              column.render({
                value,
                data,
                options:
                  knowncolumns[column.columnName] ||
                  knownColsCamel[column.columnName],
              })
            ) : (
              <>
                {column.renderRaw ? (
                  GetColRenderByType(column, value)
                ) : (
                  <Typography
                    component={"span"}
                    variant="body2"
                    className={column.callbacks ? "text-underline" : ""}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    // {...(column?.callbacks?.[columnKey]
                    //   ? {
                    //       onClick: (e) => column.callbacks[columnKey](e, data),
                    //     }
                    //   : {})}
                  >
                    {GetColRenderByType(column, value)}
                  </Typography>
                )}
                {column?.indicator && data?.isNew ? (
                  <Typography component={"span"}>{column.indicator}</Typography>
                ) : null}
                {column.callbacks &&
                column.callbacks[columnKey] &&
                column.renderCallbackButton ? (
                  <Typography component={"span"} className="preview-hover ">
                    <UIButton
                      // className="bg-base-60"
                      sx={{ height: "30px", fontSize: "0.875rem" }}
                      title={"Preview"}
                      onClick={(e) => column.callbacks[columnKey](e, data)}
                      variant={"outlined"}
                    />
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
        );
      },
    };
    columns.push(col);
  });

  return columns;
};

const isNumericCustomField = (col) => {
  const field = resolveFieldByColumnName(col.customFields, col.columnName);
  if (!field) {
    return false;
  }
  return (
    field.fieldType === Enums.CustomFieldType.Numeric ||
    field.parentType === "Numeric"
  );
};

const getNumericFilter = (col) => ({
  type: "number",
  filterEditorProps: {
    placeholder: col.header,
  },
});

const isLegacySingleSelectColumnName = (columnName = "") =>
  /CLeadCO/i.test(String(columnName));

const isLegacyMultiSelectColumnName = (columnName = "") =>
  /CMultiValue/i.test(String(columnName));

/** Same option list as multivalue custom-field column filters. */
const getCustomFieldSelectFilterDataSource = (col) =>
  generateOptionsDefault(knowncolumns.CustomFieldsOptions(col));

const createSelectFilterConfig = (dataSource) => ({
  type: "select",
  filterType: "select",
  filterEditor: SelectFilter,
  filterEditorProps: (props) => {
    const v = props?.filterValue?.value;
    const hasValue =
      v !== null &&
      v !== undefined &&
      v !== "" &&
      !(Array.isArray(v) && v.length === 0);
    return {
      placeholder: "Select",
      dataSource,
      multiple: false,
      clearIcon: hasValue,
    };
  },
});

const isCustomFieldSelectFilterColumn = (col) => {
  if (!col?.customFields?.length) {
    return false;
  }

  const columnName = String(col.columnName ?? "");
  const columnType = String(col.columnType ?? "").toLowerCase();
  const field = resolveFieldByColumnName(col.customFields, columnName);
  const hasOptionList = knowncolumns.CustomFieldsOptions(col)?.length > 0;

  if (
    columnType === "multivalue" ||
    isLegacyMultiSelectColumnName(columnName) ||
    field?.fieldType === Enums.CustomFieldType.MultiValue
  ) {
    return hasOptionList;
  }

  return (
    (field?.fieldType === Enums.CustomFieldType.SingleValue && hasOptionList) ||
    (isLegacySingleSelectColumnName(columnName) && hasOptionList) ||
    (/^\d+$/.test(columnName) && hasOptionList)
  );
};

const getCustomFieldSelectFilter = (col) => {
  if (!isCustomFieldSelectFilterColumn(col)) {
    return null;
  }
  const dataSource = getCustomFieldSelectFilterDataSource(col);
  if (!dataSource.length) {
    return null;
  }
  return createSelectFilterConfig(dataSource);
};

const getFiltersByType = (col) => {
  // Custom field column names are FieldIds (all digits). Number fields,
  // including Currency, must stay a numeric input. Option fields still use
  // the select filter below.
  if (isNumericCustomField(col)) {
    return getNumericFilter(col);
  }

  const customFieldSelectFilter = getCustomFieldSelectFilter(col);
  if (customFieldSelectFilter) {
    return customFieldSelectFilter;
  }

  switch (col.columnType.toLowerCase()) {
    case "string":
    case "phone":
    case "text": {
      return {
        type: "string",
        filterEditorProps: {
          placeholder: col.header,
        },
      };
    }
    case "number":
    case "numeric":
    case "int32":
    case "int32?": {
      if (col.filterType === "number") {
        return {
          type: "number",
          filterEditor: GridIdField,
          filterEditorProps: {
            placeholder: col.header,
          },
        };
      }

      return getNumericFilter(col);
    }
    case "date":
    case "datetime":
    case "datetime?": {
      let type = "date";
      const fieldSubType = resolveFieldByColumnName(
        col.customFields,
        col.columnName
      )?.fieldSubType;
      const isTimeType = fieldSubType == Enums.CustomFieldSubType.Time;
      const isDateAndTime =
        fieldSubType == Enums.CustomFieldSubType.DateAndTime;
      if (isTimeType) {
        type = "time";
      }
      const placeholder = isTimeType
        ? "Select Time"
        : isDateAndTime
          ? "Select Date and Time"
          : "Select Date";
      const isCustomDateField =
        !isTimeType &&
        !isDateAndTime &&
        !!resolveFieldByColumnName(col.customFields, col.columnName);
      return {
        type: type,
        dateFormat: col?.fetchDateFormat || col?.dateFormat,
        filterEditor: isTimeType
          ? TimeFilter
          : isCustomDateField
            ? GridDateFilter
            : DateFilter,
        filterEditorProps: () => {
          return {
            dateFormat: col?.fetchDateFormat || col?.dateFormat,
            placeholder,
          };
        },
      };
    }

    case "boolean":
    case "Boolean?":
    case "multivalue":
    case "tags":
    case "select": {
      let dataSource = [
        {
          id: "",
          label: "All",
        },
        {
          id: true,
          label: "Yes",
        },
        {
          id: false,
          label: "No",
        },
      ];
      if (!["Boolean?", "boolean"].includes(col.columnType)) {
        if (col.columnType.toLowerCase() == "multivalue") {
          dataSource = getCustomFieldSelectFilterDataSource(col);
        } else {
          let colOptions =
            knowncolumns[col.columnName] || knownColsCamel[col.columnName];
          dataSource = col?.dataSource
            ? col?.dataSource
            : generateOptionsDefault(colOptions ? colOptions() : []);
        }
      }

      return {
        type: "select",
        filterType: "select",
        filterEditor: SelectFilter,
        filterEditorProps: (props) => {
          const v = props?.filterValue?.value;
          const hasValue =
            v !== null &&
            v !== undefined &&
            v !== "" &&
            !(Array.isArray(v) && v.length === 0);
          return {
            placeholder: "Select",
            dataSource,
            // Column `multiple` is for cell chips only — do not multi-select the filter.
            multiple: false,
            // ComboBox treats "" as selected and always shows the clear (X) icon.
            clearIcon: hasValue,
          };
        },
      };
    }

    default: {
      return {};
    }
  }
};

const GetColRenderByType = (col, value = "") => {
  if (col?.formatAsDate) {
    return renderFormattedDate(col, value);
  }
  switch (col.columnType.toLowerCase()) {
    case "Int32":
    case "Int32?":
    case "select": {
      let colOptions =
        knowncolumns[col.columnName] || knownColsCamel[col.columnName];
      if (colOptions) {
        let res = colOptions();
        value =
          typeof value == "string" ? value.replace(".", "").trim() : value;
        let match = res.find((item) => item.value == value);

        if (!match && col.columnName === "assignedTo") {
          const users = getOptionsFromRedux("users") || [];
          const user = users.find((u) => Number(u.sourceId) === Number(value));
          if (user) {
            match = { label: user.displayName };
          }
        }

        return match ? match.label || "N/A" : "N/A";
      } else {
        return value;
      }
    }
    case "number":
    case "Number?": {
      const customFieldData = resolveFieldByColumnName(
        col.customFields,
        col?.columnName
      );
      let newValue = value;
      if (customFieldData?.leadCustomOptions?.length) {
        newValue = customFieldData.leadCustomOptions.find(
          (opt) => opt.id === +value
        )?.value;
      }
      return newValue ?? "N/A";
    }
    case "Boolean?":
    case "boolean": {
      return (
        <Chip
          size="small"
          sx={{
            fontWeight: "500",
            fontSize: "12px",
            color: value ? "var(--palette-success)" : "var(--palette-error)",
            backgroundColor: value
              ? "var(--palette-success_bg)"
              : "var(--palette-error_bg)",
            border: `none`,
          }}
          label={value ? "Yes" : "No"}
          // color={value ? "#BBE5B3" : "error"}
          variant="outlined"
        />
      );
    }
    case "date":
    case "datetime":
    case "datetime?":
    case "datetimeoffset": {
      return renderFormattedDate(col, value);
    }
    case "phone": {
      if (value) {
        return value;
      } else {
        return "N/A";
      }
    }
    case "tags": {
      if (value) {
        let tags =
          knowncolumns[col.columnName] || knownColsCamel[col.columnName];
        if (col.multiple) {
          const idKey = col.idKey || "tagId";
          const rawList = Array.isArray(value) ? value : [value];
          const tagIds = rawList.map((item) => String(item?.[idKey] ?? item));
          const selectedTags = (tags?.() || []).filter((tag) => {
            const optionIds = [tag.id, tag.value, tag.uuid, tag.calendarId]
              .filter((id) => id != null)
              .map(String);
            return optionIds.some((id) => tagIds.includes(id));
          });
          return <TagsChips tags={selectedTags} />;
        } else {
          const sourceId =
            typeof value === "object"
              ? (value?.id ?? value?.tagId ?? value?.value)
              : value;
          const selectedTag = tags().find(
            (tag) =>
              String(tag.id) === String(sourceId) ||
              String(tag.value) === String(sourceId)
          );
          const tagLabel = selectedTag?.name || selectedTag?.label || "N/A";
          let color = knowncolumns
            .tagColors()
            .find(
              (t) =>
                t.id == selectedTag?.color || t.id == selectedTag?.tagColorId
            );
          if (!color && selectedTag) {
            const opts = tags();
            const colorInd = Math.max(
              0,
              opts.findIndex(
                (tag) =>
                  String(tag.id) === String(selectedTag.id) ||
                  String(tag.value) === String(selectedTag.value)
              )
            );
            const paletteColor = GraphColors[colorInd % GraphColors.length];
            color = {
              code: paletteColor,
              textCode: "#fff",
            };
          }
          return col?.bgfill ? (
            <Typography
              component={"span"}
              variant="body2"
              sx={{
                backgroundColor: color?.code,
                color: color?.textCode,
                ...(col?.cellStyles || {}),
              }}
            >
              {tagLabel}
            </Typography>
          ) : (
            <Chip
              key={tagLabel}
              label={<TruncateTooltip text={tagLabel} limit={15} />}
              style={{
                backgroundColor: color?.code,
                color: color?.textCode,
              }}
            />
          );
        }
      } else return "N/A";
    }

    case "multivalue": {
      // this could be helpful if chips to be shown in grid
      // or remove it when you see if this comment is older than a month
      const selected =
        value && typeof value == "string" ? value?.trim()?.split(",") : "N/A";
      if (Array.isArray(selected) && selected.length > 0 && false) {
        return selected.map((val) => <Chip label={val} key={val} />);
      }
      // till here
      return value || "N/A";
    }

    default: {
      return value
        ? value
        : value === false
          ? "false"
          : value === 0
            ? value
            : "N/A";
    }
  }
};

export const getOptionsFromRedux = (key = "") => {
  let { dropdownOptions } = store.getState().dropdownOptions;
  return dropdownOptions[key];
};

export const OptionsWithColor = (array = []) => {
  let colorInd = 0;
  return array.map((arr) => {
    let color = GraphColors[colorInd];
    if (colorInd == GraphColors.length - 1) {
      colorInd = 0;
    } else {
      colorInd++;
    }
    return { ...arr, color: color };
  });
};

export const knowncolumns = {
  CrmMembers: () =>
    generateOptions(getOptionsFromRedux("crmMembers"), "name", "id"),
  CrmCalendars: () =>
    generateOptions(getOptionsFromRedux("crmCalendars"), "name", "id"),
  CustomFieldsOptions: (col) => {
    if (!col.customFields?.length) {
      return [];
    }
    const field = resolveFieldByColumnName(col.customFields, col.columnName);
    if (!field) {
      return [];
    }
    if (field.options?.length) {
      return field.options;
    }
    if (field.leadCustomOptions?.length) {
      return field.leadCustomOptions.map((option) => ({
        ...option,
        id: option.id,
        label: option.value,
        value: option.id,
      }));
    }
    return [];
  },
  Salutation: () => generateOptions(enum_salutations, "name", "value"),
  initiatedBy: () =>
    generateOptions(dropdownEnums.enum_InitiatedByForGrid, "label", "id"),
  LeadOriginSubTypeId: () =>
    generateOptions(getOptionsFromRedux("leadOriginSubType"), "name", "id"),
  LeadTypeId: () =>
    generateOptions(dropdownEnums.enum_LeadType, "name", "value"),
  SourceType: () =>
    generateOptions(dropdownEnums.enum_Source_Type, "name", "value"),
  status: () =>
    generateOptions(dropdownEnums.enum_importLeadStatus, "name", "value"),
  source: () =>
    generateOptions(dropdownEnums.enum_importLeadSource, "name", "value"),
  LeadOwnerId: () =>
    generateOptions(
      OptionsWithColor(getOptionsFromRedux("users")),
      "displayName",
      "id"
    ),
  LeadContactedUsers: () =>
    generateOptions(
      getOptionsFromRedux("leadContactedUsers"),
      "displayName",
      "id"
    ),
  AssignedTo: () =>
    generateOptions(getOptionsFromRedux("users"), "displayName", "id"),
  UserName: () =>
    generateOptions(getOptionsFromRedux("users"), "username", "id"),
  UserDisplayNameWithEmail: () =>
    generateOptions(
      getOptionsFromRedux("users").map((user) => {
        const name =
          user.displayName ||
          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
          user.username ||
          "";
        const email = user.email || user.ssoEmail || "";
        const displayName =
          name && email ? `${name} (${email})` : name || email || "Unknown";
        return { ...user, displayName };
      }),
      "displayName",
      "id"
    ),
  userId: () =>
    generateOptions(
      OptionsWithColor(getOptionsFromRedux("users")),
      "displayName",
      "id"
    ),
  memberId: () => {
    const members = getOptionsFromRedux("members") || [];
    const normalizedMembers = members.map((member) => {
      const parsedIntId = Number(member?.intId ?? member?.Id ?? member?.id);
      const intId = Number.isFinite(parsedIntId) ? parsedIntId : null;
      const reportMemberId = intId ?? member?.id;

      return {
        ...member,
        intId,
        reportMemberId,
        // ExpressionRenderer uses dt.id when saving checkbox-list values.
        id: reportMemberId,
      };
    });

    return generateOptions(normalizedMembers, "name", "reportMemberId");
  },
  LeadStatusId: () =>
    generateOptions(getOptionsFromRedux("leadStatuses"), "name", "id"),
  BasicLeadSource: () => {
    const basicSources = dropdownEnums.enum_BasicLeadSource || [];
    return basicSources;
  },
  LeadSourceId: () => {
    const reduxSources = getOptionsFromRedux("leadSources") || [];
    const basicSources = dropdownEnums.enum_BasicLeadSource || [];
    const mappedBasic = basicSources.map((s) => ({
      id: s.value,
      name: s.name,
    }));
    // const combined = [...mappedBasic];
    const combined = [];
    reduxSources.forEach((rs) => {
      const nameLower = (rs.name || "").toLowerCase();
      const idLower = String(rs.id || "").toLowerCase();
      if (nameLower === "crmflow" || idLower === "crmflow") {
        return;
      }
      const exists = combined.some(
        (cs) =>
          cs.name?.toLowerCase() === nameLower ||
          String(cs.id).toLowerCase() === idLower
      );
      if (!exists) {
        combined.push(rs);
      }
    });
    const filtered = combined.filter((item) => {
      const name = (item.name || "").toLowerCase();
      const id = String(item.id || "").toLowerCase();
      return name !== "crmflow" && id !== "crmflow";
    });
    return generateOptions(filtered, "name", "id");
  },
  LanguageId: () =>
    generateOptions(getOptionsFromRedux("languages"), "name", "id"),
  PreferredContactId: () =>
    generateOptions(getOptionsFromRedux("preferredContact"), "name", "id"),
  LeadTags: () => generateOptions(getOptionsFromRedux("tags"), "name", "id"),
  LeadAppointments: () =>
    generateOptions(getOptionsFromRedux("crmCalendars"), "name", "id"),
  businessCategoryIds: () =>
    generateOptions(getOptionsFromRedux("businessCategory"), "name", "id"),
  industryId: () =>
    generateOptions(getOptionsFromRedux("industries"), "name", "id"),
  workflows: () =>
    generateOptions(
      OptionsWithColor(getOptionsFromRedux("workFlows")),
      "name",
      "id"
    ),
  // appointmentStatus: () =>
  //   generateOptions(
  //     dropdownEnums.enum_Leadenum_appointmentStatusType,
  //     "name",
  //     "value"
  //   ),
  createdBy: () =>
    generateOptions(getOptionsFromRedux("users"), "displayName", "id"),
  CountryId: (numericId) =>
    generateOptions(
      getOptionsFromRedux("countries"),
      "countryName",
      numericId ? "id" : "countryId"
    ),
  StateProvinceId: (numericId) =>
    generateOptions(
      getOptionsFromRedux("statesProvinces"),
      "stateProvinceName",
      numericId ? "id" : "stateProvinceId"
    ),
  TaskTypeId: () =>
    generateOptions(getOptionsFromRedux("taskTypes"), "name", "id"),
  TaskPriorityId: () =>
    generateOptions(getOptionsFromRedux("taskPriorities"), "name", "id"),
  TaskStatusId: () =>
    generateOptions(getOptionsFromRedux("taskStatuses"), "name", "id"),
  tagColors: () =>
    generateOptions(getOptionsFromRedux("tagColors"), "name", "id"),
  categoryId: () =>
    generateOptions(
      getOptionsFromRedux("reportTemplateCategory"),
      "name",
      "id"
    ),
  CallResult: () =>
    generateOptions(
      getOptionsFromRedux("callResult"),
      "value",
      "id",
      undefined,
      "heading"
    ).map((cp) => ({ ...cp, label: cp.label.replace("_", " / ") })),
  CallPurpose: () =>
    generateOptions(
      getOptionsFromRedux("callPurpose"),
      "value",
      "id",
      undefined,
      "heading"
    ).map((cp) => ({ ...cp, label: cp.label.replace("_", " / ") })),
  RepresentativeName: () =>
    generateOptions(
      getOptionsFromRedux("representativesName"),
      "representativeName",
      "representativeName"
    ),
};

export const knownColsCamel = {
  salutation: knowncolumns.Salutation,
  crmMembers: knowncolumns.CrmMembers,
  initiatedBy: knowncolumns.initiatedBy,
  leadOriginSubTypeId: knowncolumns.LeadOriginSubTypeId,
  leadTypeId: knowncolumns.LeadTypeId,
  leadOwnerId: knowncolumns.LeadOwnerId,
  assignedTo: knowncolumns.AssignedTo,
  userId: knowncolumns.userId,
  leadStatusId: knowncolumns.LeadStatusId,
  leadSourceId: knowncolumns.LeadSourceId,
  sourceType: knowncolumns.SourceType,
  languageId: knowncolumns.LanguageId,
  status: knowncolumns.status,
  source: knowncolumns.source,
  preferredContactId: knowncolumns.PreferredContactId,
  tagIds: knowncolumns.LeadTags,
  leadAppointments: knowncolumns.LeadAppointments,
  customFieldsOptions: (col) => knowncolumns.CustomFieldsOptions(col),
  industries: knowncolumns.industryId,
  workflows: knowncolumns.workflows,
  leadContactedUsers: knowncolumns.LeadContactedUsers,
  memberId: knowncolumns.memberId,
  countryId: knowncolumns.CountryId,
  stateProvinceId: knowncolumns.StateProvinceId,
  taskPriorityId: knowncolumns.TaskPriorityId,
  taskTypeId: knowncolumns.TaskTypeId,
  taskStatusId: knowncolumns.TaskStatusId,
  tagColors: knowncolumns.tagColors,
  categoryId: knowncolumns.categoryId,
  representativeName: knowncolumns.RepresentativeName,
  basicLeadSource: knowncolumns.BasicLeadSource,
};

export const GenerateActionButtons = (
  Comp,
  props = { header: "Action", width: 60 },
  position = "end"
) => {
  const edge = position === "start" ? "start" : "end";
  const frozenCellClass = `grid-frozen-action-cell grid-frozen-action-cell--${edge}`;
  const frozenHeaderClass = `grid-frozen-action-header grid-frozen-action-header--${edge}`;
  const frozenWrapperClass = `grid-frozen-action-resize-wrapper grid-frozen-action-resize-wrapper--${edge}`;

  return {
    name: "actions",
    header: getHeader(props.header || "", false),
    minWidth: props.width,
    // defaultWidth: 80,
    // defaultFlex: 2,
    resizeable: false,
    showColumnMenuTool: false,
    sortable: false,
    draggable: false,
    ...props,
    filter: false,
    enableFilter: false,
    headerWrapperClassName: [props.headerWrapperClassName, frozenWrapperClass]
      .filter(Boolean)
      .join(" "),
    className: [props.className, frozenCellClass].filter(Boolean).join(" "),
    headerProps: {
      ...(props.headerProps || {}),
      className: [props.headerProps?.className, frozenHeaderClass]
        .filter(Boolean)
        .join(" "),
    },
    visible: true,
    render: (value) => {
      return (
        <Box
          sx={{ display: "flex", gap: "10px", justifyContent: "center", px: 1 }}
        >
          {Comp ? <Comp value={value?.data || {}} /> : <></>}
        </Box>
      );
    },
  };
};
